import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  Col,
  Button,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Spinner,
  Label,
  Input,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import _ from 'lodash';
import { Redirect, Link, useLocation } from 'react-router-dom';
import { DidomiSDK } from '@didomi/react';
import Flex from '../common/Display/Flex';
import Alert from '../common/Alert';
import Translate, { t } from '../common/Translate';
import { didomiC } from '../../config';
import { flags, languages } from '../../language-context';
import ApiService from '../../services/api/ApiService';
import logo from '../../assets/img/logo_app.png';
import cacheStaticContent from '../../services/Auth/cacheStaticContent';
import useAuth from '../../hooks/useAuth0/useAuth';
import { Auth } from '../../config/configuration/react';

const errors = {
  401: 'user_unauthorized',
  404: 'route_not_found',
  403: 'access_refuse',
  400: 'bad_request',
  405: 'api_error_registration_unkown',
  498: 'token expired/invalid',
  500: 'internal_server_error',
  504: 'server_not_responding',
  default: 'api_error_unknown',
};

const didomiConfig = {
  app: {
    name: didomiC.name,
    apiKey: didomiC.apiKey,
  },
  languages: {
    enabled: [Auth.preferredLanguage() || didomiC.languages],
  },
};

const Login = () => {
  const location = useLocation();
  const [state, setState] = useState({
    isUserLogged: null,
    error: '',
    showError: false,
    toggle: false,
    nrmUser: false,
  });
  const [didomiObject, setDidomiObject] = useState({});
  const { authStatus, error: authError, loginWithRedirect, logout, isAuthenticated } = useAuth();
  const { from } = location.state || { from: { pathname: '/forms' } };
  useEffect(() => {
    if (authStatus === 'success') setState({ ...state, isUserLogged: true, showError: false });
    else if (authStatus === 'not-authenticated' && !isAuthenticated && !authError)
      loginWithRedirect({
        appState: {
          returnTo: from,
        },
      });
    else if (authError) setState({ ...state, isUserLogged: false, showError: true, error: authError });
  }, [authStatus]);

  const handleRefreshAuth0 = async () => {
    await logout({
      logoutParams: {
        returnTo: window.location.origin,
        federated: true,
      },
    });
  };

  const onDidomiReady = (didomi) => {
    setDidomiObject(didomi);
  };

  let alertAuth;
  const { isUserLogged, showError, error, nrmUser } = state;

  if (showError) {
    alertAuth = (
      <Alert color="danger" visible hideOfflineAlert={false}>
        {error}
      </Alert>
    );
  } else if (isUserLogged === null) {
    return (
      <>
        <div className="loader-full-screen main img-loader">
          <div>
            <img src={logo} alt="" />
            <div>
              <Spinner type="grow" color="secondary" />
              <Spinner type="grow" color="secondary" />
              <Spinner type="grow" color="secondary" />
            </div>
          </div>
        </div>
      </>
    );
  } else if (isUserLogged) {
    if (nrmUser) {
      return <Redirect to="/dashboardnrm" />;
    }
    // return <Redirect to="/forms" />;
  }

  return (
    <>
      <DidomiSDK apiKey={didomiC.apiKey} noticeId={didomiC.noticeId} config={didomiConfig} onReady={onDidomiReady} />
      <Flex noBack match={{ path: '/' }}>
        <Container className="login-page h-100">
          {alertAuth}
          <div className="text-center mt-4">
            <Button className="btn btn-1" onClick={handleRefreshAuth0}>
              <Translate code="refresh" />
            </Button>
          </div>
        </Container>
        <Container className="footer mb-2">
          <div className="footer-lang">
            <LanguageFooter />
          </div>
          <div className="footer-bloc">
            <Button className="mb-1" color="link" onClick={() => didomiObject.preferences.show()}>
              <Translate code="consentChoices" />
            </Button>
            <Link to="content/privacyPolicy">
              <Translate code="privacyPolicy" />
            </Link>
          </div>
        </Container>
      </Flex>
    </>
  );
};

const LanguageFooter = () => {
  const [isOpen, setIsOpen] = useState(false);
  const ENV = process.env.REACT_APP_ENV;
  const country = _.upperCase(Auth.preferredLanguage());
  const toggle = () => setIsOpen((prevState) => !prevState);

  const handleChangeLang = async (language) => {
    window.dispatchEvent(new CustomEvent('changeLanguage', { detail: { language } }));
    await cacheStaticContent(language);
    if (window.location.href.includes('content')) window.location.reload();
  };

  return (
    <>
      <Dropdown isOpen={isOpen} toggle={toggle}>
        <DropdownToggle size="sm" caret>
          <Translate code="languages" />
          <img src={_.get(flags, country.toLowerCase(), 'fr')} className="icon-flag ml-2 mr-2" alt="" /> {country}
        </DropdownToggle>
        <DropdownMenu>
          {languages.map((lang) => {
            if (lang.language_iso2 === country) return '';
            return (
              <DropdownItem key={lang.language_iso2} onClick={() => handleChangeLang(lang.language_iso2)}>
                <img src={flags[lang.language_iso2]} className="icon-flag" alt="" />
                <span className="ml-2">{_.toUpper(lang.language_iso2)}</span>
              </DropdownItem>
            );
          })}
          {ENV !== 'prod' && (
            <DropdownItem key="var" onClick={() => handleChangeLang('var')}>
              <>
                <img src={flags.var} className="icon-flag" alt="" />
                <span className="ml-2">{_.toUpper('var')}</span>
              </>
            </DropdownItem>
          )}
        </DropdownMenu>
      </Dropdown>
    </>
  );
};

const ResetPassword = ({ toggle, handleToggle, login }) => {
  const [username, setUsername] = useState(login || null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const resetPassword = async () => {
    setIsLoading(true);
    setSuccess(null);
    setError(null);
    try {
      const params = {
        username,
      };
      const result = await ApiService('resetPassword', null, null, JSON.stringify(params));
      if ('data' in result && 'message' in result.data) {
        setSuccess(_.get(result, 'data.message', ''));
        setError(null);
      }
    } catch (e) {
      if (e.response.status in errors) setError(errors[e.response.status]);
      setError(errors.default);
      setSuccess(null);
    }
    setIsLoading(false);
  };

  return (
    <Modal isOpen={toggle} toggle={handleToggle}>
      <ModalHeader toggle={handleToggle} className="model-title">
        <Translate code="forgot_password" />
      </ModalHeader>
      <ModalBody>
        <Row className="mb-4">
          <Col xs="12">
            <Label className="mb-2 text-primary text-left" for="username">
              <Translate code="login" />
            </Label>
            <Input
              className="form-control rounded"
              type="text"
              name="username"
              id="username"
              onChange={(e) => setUsername(e.target.value)}
              value={username || ''}
            />
          </Col>
        </Row>

        <button
          type="button"
          className={`btn btn-block ${username && !isLoading ? 'btn-1' : 'btn-4'}`}
          onClick={resetPassword}
          disabled={!(username && !isLoading)}
        >
          {t('valider')}
        </button>

        <Row className="mb-4 reset-password">
          <Col xs="12">
            {isLoading && <Spinner color="primary" />}
            {error && (
              <div>
                <br />
                <span className="error">{t('email_reset_not_sent')}</span>
              </div>
            )}
            {success && (
              <div>
                <br />
                <span className="success">{t('email_reset_sent')}</span>
              </div>
            )}
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  );
};

ResetPassword.propTypes = {
  toggle: PropTypes.bool.isRequired,
  handleToggle: PropTypes.func.isRequired,
  login: PropTypes.string.isRequired,
};

export default Login;
